/**
 * CA Status Chip Component
 * Displays a colored chip based on the CA (Certificado de Aprovação) validity status
 *
 * @version 2025.1.0
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as ValidIcon,
  Warning as ExpiringSoonIcon,
  Error as ExpiredIcon,
} from '@mui/icons-material';
import { isCaExpired, isCaExpiringSoon, getDaysUntilExpiration } from '../../../types/epi';

interface CaStatusChipProps {
  fimValidade: string;
  size?: 'small' | 'medium';
}

export const CaStatusChip: React.FC<CaStatusChipProps> = ({
  fimValidade,
  size = 'small',
}) => {
  const expired = isCaExpired(fimValidade);
  const expiringSoon = isCaExpiringSoon(fimValidade);
  const daysLeft = getDaysUntilExpiration(fimValidade);

  if (expired) {
    return (
      <Tooltip title={`CA vencido há ${Math.abs(daysLeft)} dias`}>
        <Chip
          icon={<ExpiredIcon />}
          label="CA Vencido"
          size={size}
          color="error"
        />
      </Tooltip>
    );
  }

  if (expiringSoon) {
    return (
      <Tooltip title={`CA vence em ${daysLeft} dias`}>
        <Chip
          icon={<ExpiringSoonIcon />}
          label={`Vence em ${daysLeft}d`}
          size={size}
          color="warning"
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`CA válido por mais ${daysLeft} dias`}>
      <Chip
        icon={<ValidIcon />}
        label="CA Válido"
        size={size}
        color="success"
        variant="outlined"
      />
    </Tooltip>
  );
};

export default CaStatusChip;
